import { useState, useEffect } from "react";
import GameLayout from "../components/GameLayout";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Pickaxe, Gem, Wallet, Users, Zap, TrendingUp, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import DailyReward from "../components/DailyReward";
import ReferralWidget from "../components/ReferralWidget";

function StatCard({ icon: Icon, label, value, color, link }) {
  const content = (
    <div className="p-6 bg-[#131722] rounded-xl border border-[#2A3241]/40 hover:border-opacity-100 transition-all duration-200 hover:-translate-y-0.5">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <p className="font-mono-game text-2xl font-bold text-[#F8FAFC]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
      <p className="text-xs text-[#64748B] mt-1 tracking-wide uppercase">{label}</p>
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [mining, setMining] = useState(null);
  const [worker, setWorker] = useState(null);
  const [invCount, setInvCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [mRes, pRes] = await Promise.all([
          API.get("/mining/status"),
          API.get("/profile"),
        ]);
        setMining(mRes.data.session);
        setWorker(mRes.data.worker);
        setInvCount(pRes.data.inventory_count);
      } catch {}
    };
    load();
  }, []);

  const miningActive = mining?.active;
  let miningHours = 0;
  if (miningActive && mining?.last_collected) {
    const elapsed = (Date.now() - new Date(mining.last_collected).getTime()) / 3600000;
    miningHours = Math.min(elapsed, 12).toFixed(1);
  }

  return (
    <GameLayout title="Dashboard">
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome */}
        <div className="relative bg-[#131722] rounded-2xl border border-[#2A3241]/40 overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/9ff327805df8bb7007aaa05b60bb28f3aec867c046d8fea9b6579e84c4c6e114.png" alt="" className="w-full h-full object-cover opacity-10" />
          </div>
          <div className="relative p-8 flex items-center gap-6">
            <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/8d6e3f7efeac3bec6e15aa0d3009ebcea428e7a2b007276162970a447bd2d82c.png"
              alt="character" className="w-20 h-20 rounded-xl object-cover border border-[#2A3241] hidden sm:block" />
            <div>
              <p className="text-xs font-mono-game text-[#00F0FF] tracking-[0.2em] uppercase mb-2">Welcome back</p>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F8FAFC] mb-2">{user?.name}</h1>
              <p className="text-sm text-[#94A3B8]">
                {miningActive ? (
                  <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#10B981] animate-mining-pulse" /> Workers active &middot; {miningHours}h accumulated</span>
                ) : "Your workers are idle. Send them to dig!"}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Gem} label="Coins" value={user?.coins?.toFixed(2) || "0.00"} color="#FFD600" link="/wallet" />
          <StatCard icon={Wallet} label="Withdrawable" value={`$${user?.withdrawable_balance?.toFixed(2) || "0.00"}`} color="#10B981" link="/wallet" />
          <StatCard icon={Pickaxe} label="Deposit Balance" value={`$${user?.deposit_balance?.toFixed(2) || "0.00"}`} color="#00F0FF" link="/wallet" />
          <StatCard icon={Users} label="Inventory Items" value={invCount} color="#B026FF" link="/inventory" />
        </div>

        {/* Worker Overview */}
        {worker && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#F8FAFC]">Worker Status</h3>
              <Link to="/workers" className="text-xs text-[#00F0FF] hover:underline">Manage</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Energy", value: worker.energy, max: worker.max_energy, color: "#00F0FF" },
                { label: "Hunger", value: worker.hunger, max: 100, color: "#FFD600" },
                { label: "Fatigue", value: worker.fatigue, max: 100, color: "#EF4444", invert: true },
                { label: "Efficiency", value: (worker.efficiency * 100).toFixed(0), max: 100, color: "#10B981", suffix: "%" },
              ].map(({ label, value, max, color, invert, suffix }) => (
                <div key={label} className="text-center">
                  <p className="text-xs text-[#64748B] tracking-wide uppercase mb-2">{label}</p>
                  <div className="h-2 bg-[#0B0E14] rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full transition-all duration-1000" style={{
                      width: `${invert ? (1 - value / max) * 100 : (value / max) * 100}%`,
                      backgroundColor: color
                    }} />
                  </div>
                  <p className="font-mono-game text-sm text-[#F8FAFC]" data-testid={`worker-${label.toLowerCase()}`}>{value}{suffix || `/${max}`}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Reward + Referral */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DailyReward />
          <ReferralWidget />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/mining" data-testid="quick-mining" className="p-4 bg-[#131722] rounded-xl border border-[#2A3241]/40 hover:border-[#00F0FF]/30 transition-all flex items-center gap-3">
            <Pickaxe className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <p className="text-sm font-medium text-[#F8FAFC]">Start Digging</p>
              <p className="text-xs text-[#64748B]">Send workers to extract</p>
            </div>
          </Link>
          <Link to="/tasks" data-testid="quick-tasks" className="p-4 bg-[#131722] rounded-xl border border-[#2A3241]/40 hover:border-[#FFD600]/30 transition-all flex items-center gap-3">
            <Zap className="w-5 h-5 text-[#FFD600]" />
            <div>
              <p className="text-sm font-medium text-[#F8FAFC]">Daily Tasks</p>
              <p className="text-xs text-[#64748B]">Complete for bonus coins</p>
            </div>
          </Link>
          <Link to="/inventory" data-testid="quick-inventory" className="p-4 bg-[#131722] rounded-xl border border-[#2A3241]/40 hover:border-[#B026FF]/30 transition-all flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-[#B026FF]" />
            <div>
              <p className="text-sm font-medium text-[#F8FAFC]">Sell Resources</p>
              <p className="text-xs text-[#64748B]">Convert to coins</p>
            </div>
          </Link>
        </div>
      </div>
    </GameLayout>
  );
}
